import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { PayrollFormModal } from '../components/PayrollFormModal';
import { SearchableDropdown } from '../components/SearchableDropdown';
import { useAuth } from '../context/AuthContext';
import { payrollsApi, type Payroll, type PayrollPaging, type PayrollStatus, type PayrollsSummary } from '../lib/api/payrolls';
import { departmentsApi } from '../lib/api/departments';
import { exportSheet, fmtDateISO } from '../lib/export-generic';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';

const STATUS_LABEL: Record<PayrollStatus, string> = {
  DRAFT: 'Nháp', PENDING_KTT: 'Chờ KTT', PENDING_CEO: 'Chờ CEO ký',
  APPROVED: 'Đã duyệt', DISBURSED: 'Đã chi', REJECTED: 'Từ chối',
};

const STATUS_TONE: Record<PayrollStatus, string> = {
  DRAFT: 'gray', PENDING_KTT: 'yellow', PENDING_CEO: 'blue',
  APPROVED: 'green', DISBURSED: 'green', REJECTED: 'red',
};

const PAYROLL_COLUMNS_STORAGE_KEY = 'erp_ac_payrolls_columns';
type PayrollColumnKey = 'period' | 'department' | 'type' | 'employeeCount' | 'basicSalary' | 'net' | 'creator' | 'createdAt' | 'status';
type PayrollColumnPrefs = Record<PayrollColumnKey, boolean>;
const DEFAULT_PAYROLL_COLUMNS: PayrollColumnPrefs = {
  period: true, department: true, type: true, employeeCount: true, basicSalary: true,
  net: true, creator: true, createdAt: true, status: true,
};
const PAYROLL_COLUMN_LABELS: Record<PayrollColumnKey, string> = {
  period: 'Kỳ lương', department: 'Bộ phận', type: 'Loại bảng lương', employeeCount: 'Số nhân viên',
  basicSalary: 'Lương cơ bản', net: 'Thực nhận', creator: 'Người lập', createdAt: 'Ngày lập', status: 'Trạng thái',
};

function deptColor(dept: string): string {
  const palette = ['#2563EB', '#7c3aed', '#dc2626', '#16a34a', '#f59e0b', '#0d9488', '#0891b2', '#ea580c'];
  const h = dept.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return palette[h % palette.length];
}

function initials(name: string): string {
  return (name || '?').split(/\s+/).slice(-2).map((w) => w[0]).join('').toUpperCase();
}

function avatarColor(name: string): string {
  const palette = ['#2563EB', '#7c3aed', '#dc2626', '#16a34a', '#f59e0b', '#0d9488'];
  const h = name.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return palette[h % palette.length];
}

interface ActiveFilters {
  year: number | null;
  status: 'processing' | 'disbursed' | null;
  department: string | null;
  type: string | null;
}

export function PayrollPage() {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [baseDepartments, setBaseDepartments] = useState<string[]>([]);
  const [paging, setPaging] = useState<PayrollPaging | null>(null);
  const [summary, setSummary] = useState<PayrollsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [filters, setFilters] = useState<ActiveFilters>({ year: 2026, status: 'processing', department: null, type: null });
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<ActiveFilters>(filters);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const navigate = useNavigate();
  const openDetail = (id: string) => navigate(`/payroll/${id}`);
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [columns, setColumns] = useState<PayrollColumnPrefs>(() => {
    try {
      const saved = localStorage.getItem(PAYROLL_COLUMNS_STORAGE_KEY);
      if (!saved) return DEFAULT_PAYROLL_COLUMNS;
      if (saved === 'creator-hidden') return { ...DEFAULT_PAYROLL_COLUMNS, creator: false };
      return { ...DEFAULT_PAYROLL_COLUMNS, ...(JSON.parse(saved) as Partial<PayrollColumnPrefs>) };
    } catch { return DEFAULT_PAYROLL_COLUMNS; }
  });
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsWrapRef = useRef<HTMLDivElement>(null);

  const isKtt = user?.role === 'KTT';
  const isCeo = user?.role === 'CEO';

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [p, s] = await Promise.all([
        payrollsApi.list({
          page, limit: pageSize,
          search: search.trim() || undefined,
          year: filters.year ?? undefined,
          status: filters.status ?? undefined,
          department: filters.department ?? undefined,
          type: filters.type ?? undefined,
        }),
        payrollsApi.summary(),
      ]);
      setPayrolls(p.items); setPaging(p.paging); setSummary(s);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [page, pageSize, search, filters.year, filters.status, filters.department, filters.type]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    departmentsApi.list()
      .then(({ items }) => setBaseDepartments(items.map((item) => item.name)))
      .catch(() => setBaseDepartments([]));
  }, []);
  // Reset về trang 1 khi filter/search đổi
  useEffect(() => { setPage(1); }, [search, filters.year, filters.status, filters.department, filters.type]);

  useEffect(() => {
    const close = () => setMenuOpenFor(null);
    if (menuOpenFor) window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuOpenFor]);

  useEffect(() => {
    try { localStorage.setItem(PAYROLL_COLUMNS_STORAGE_KEY, JSON.stringify(columns)); }
    catch { /* ignore unavailable storage */ }
  }, [columns]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!columnsWrapRef.current?.contains(event.target as Node)) setColumnsOpen(false);
    };
    if (columnsOpen) window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [columnsOpen]);

  // BE handles filtering + pagination; FE hiển thị nguyên response.
  const filtered = payrolls;

  const selectedRows = payrolls.filter((p) => selected.includes(p.id));
  const totalSelectedNet = selectedRows.reduce((s, r) => s + r.net, 0);

  const bulkApprove = async () => {
    for (const id of selected) {
      try { await payrollsApi.approve(id); } catch (e) { showToast(`Lỗi: ${(e as Error).message}`); }
    }
    setSelected([]); showToast(`Đã duyệt ${selected.length} bảng`); load();
  };

  const bulkSubmit = async () => {
    const drafts = selectedRows.filter((row) => row.status === 'DRAFT');
    if (!drafts.length) { showToast('Chỉ bảng lương Nháp mới có thể trình ký.'); return; }
    const results = await Promise.allSettled(drafts.map((row) => payrollsApi.submit(row.id)));
    const done = results.filter((result) => result.status === 'fulfilled').length;
    showToast(`Đã trình ký ${done}/${drafts.length} bảng lương Nháp`);
    setSelected([]); load();
  };

  const filterChips: Array<{ key: string; label: string; onRemove: () => void }> = [];
  if (filters.year) filterChips.push({
    key: 'year',
    label: `Năm: ${filters.year}`,
    onRemove: () => setFilters({ ...filters, year: null }),
  });
  if (filters.status === 'processing') filterChips.push({
    key: 'status',
    label: 'Trạng thái: Đang xử lý',
    onRemove: () => setFilters({ ...filters, status: null }),
  });
  if (filters.status === 'disbursed') filterChips.push({
    key: 'status',
    label: 'Trạng thái: Đã chi',
    onRemove: () => setFilters({ ...filters, status: null }),
  });
  if (filters.department) filterChips.push({
    key: 'department',
    label: `Bộ phận: ${filters.department}`,
    onRemove: () => setFilters({ ...filters, department: null }),
  });
  if (filters.type) filterChips.push({
    key: 'type',
    label: `Loại: ${filters.type}`,
    onRemove: () => setFilters({ ...filters, type: null }),
  });

  const filterDepartments = Array.from(new Set([...baseDepartments, ...payrolls.map((item) => item.department)])).filter(Boolean).sort();
  const filterTypes = Array.from(new Set(['Chính thức', 'Thử việc', 'Khoán', ...payrolls.map((item) => item.type)])).filter(Boolean).sort();
  const filterYears = Array.from(new Set([2024, 2025, 2026, 2027, ...payrolls.map((item) => Number(item.period.split('/')[1]))])).filter(Number.isFinite).sort((a, b) => b - a);
  const filterYearOptions = [{ value: '', label: 'Tất cả năm' }, ...filterYears.map((year) => ({ value: String(year), label: String(year) }))];
  const filterStatusOptions = [{ value: '', label: 'Tất cả trạng thái' }, { value: 'processing', label: 'Đang xử lý' }, { value: 'disbursed', label: 'Đã chi' }];
  const filterDepartmentOptions = [{ value: '', label: 'Tất cả bộ phận' }, ...filterDepartments.map((department) => ({ value: department, label: department }))];
  const filterTypeOptions = [{ value: '', label: 'Tất cả loại' }, ...filterTypes.map((type) => ({ value: type, label: type }))];
  const openFilters = () => { setFilterDraft(filters); setFilterOpen(true); };

  return (
    <AppShell title="Bảng lương" subtitle="Quản lý bảng lương theo kỳ · Duyệt · Chi lương · Hạch toán">
      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      {/* KPI cards — icon circle right, tinted bg (match prototype) */}
      <div className="pyr-kpi-grid">
        <div className="pyr-kpi pyr-kpi-blue">
          <div className="pyr-kpi-top">
            <div className="pyr-kpi-body">
              <div className="pyr-kpi-label">Kỳ lương hiện tại</div>
              <div className="pyr-kpi-value">{summary?.currentPeriod ?? '—'}</div>
            </div>
            <div className="pyr-kpi-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" /></svg>
            </div>
          </div>
          <div className="pyr-kpi-sub">
            {summary
              ? summary.daysUntilPeriodEnd === 0
                ? 'Kết thúc hôm nay'
                : `Kết thúc trong ${summary.daysUntilPeriodEnd} ngày`
              : '—'}
          </div>
        </div>
        <div className="pyr-kpi pyr-kpi-green">
          <div className="pyr-kpi-top">
            <div className="pyr-kpi-body">
              <div className="pyr-kpi-label">Tổng bảng lương năm nay</div>
              <div className="pyr-kpi-value">{summary?.totalCount ?? '—'} <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 500 }}>bảng</span></div>
            </div>
            <div className="pyr-kpi-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7" /><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 1-9 9 9 9 0 0 1-6-2.3L3 21" /></svg>
            </div>
          </div>
          <div className="pyr-kpi-sub">{summary?.disbursedCount ?? 0} đã chi lương · {summary?.inProgressCount ?? 0} đang xử lý</div>
        </div>
        <div className="pyr-kpi pyr-kpi-yellow">
          <div className="pyr-kpi-top">
            <div className="pyr-kpi-body">
              <div className="pyr-kpi-label">Quỹ lương chi năm nay</div>
              <div className="pyr-kpi-value">{summary ? formatVND(summary.totalPaidYtd) : '—'}</div>
            </div>
            <div className="pyr-kpi-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" x2="12" y1="2" y2="22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
            </div>
          </div>
          <div className="pyr-kpi-sub">
            {summary?.paidGrowthPct != null
              ? `${summary.paidGrowthPct > 0 ? '+' : ''}${summary.paidGrowthPct}% so với năm trước`
              : 'Chưa có dữ liệu năm trước'}
          </div>
        </div>
        <div className="pyr-kpi pyr-kpi-red">
          <div className="pyr-kpi-top">
            <div className="pyr-kpi-body">
              <div className="pyr-kpi-label">Cần xử lý</div>
              <div className="pyr-kpi-value">{summary?.needActionCount ?? '—'} <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 500 }}>bảng</span></div>
            </div>
            <div className="pyr-kpi-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" x2="12" y1="8" y2="12" /><line x1="12" x2="12.01" y1="16" y2="16" /></svg>
            </div>
          </div>
          <div className="pyr-kpi-sub">{summary?.pendingKttCount ?? 0} chờ KTT duyệt · {summary?.pendingCeoCount ?? 0} chờ CEO ký</div>
        </div>
      </div>

      <div className="pay-page-card">
        <div className="pay-toolbar">
          <div className="pay-toolbar-left">
            <div className="apr-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
              <input placeholder="Tìm mã bảng, bộ phận…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button type="button" className="pay-btn-outline" onClick={openFilters}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" x2="14" y1="4" y2="4" /><line x1="10" x2="3" y1="4" y2="4" /><line x1="21" x2="12" y1="12" y2="12" /><line x1="8" x2="3" y1="12" y2="12" /><line x1="21" x2="16" y1="20" y2="20" /><line x1="12" x2="3" y1="20" y2="20" /><line x1="14" x2="14" y1="2" y2="6" /><line x1="8" x2="8" y1="10" y2="14" /><line x1="16" x2="16" y1="18" y2="22" /></svg>
              Bộ lọc
              {filterChips.length > 0 && <span className="apr-filter-badge">{filterChips.length}</span>}
            </button>
          </div>
          <div className="pay-toolbar-right">
            <button
              className="pay-btn-outline"
              type="button"
              onClick={() => {
                exportSheet('payrolls', filtered, [
                  { header: 'Mã bảng', key: 'code', width: 20 },
                  { header: 'Kỳ', key: 'period', width: 12 },
                  { header: 'Bộ phận', key: 'department', width: 20 },
                  { header: 'Loại', key: 'type', width: 14 },
                  { header: 'Số NV', key: 'employeeCount', width: 8 },
                  { header: 'Lương cơ bản', key: 'basicSalary', width: 18, format: (v) => Number(v) },
                  { header: 'Gross', key: 'gross', width: 18, format: (v) => Number(v) },
                  { header: 'Thực nhận', key: 'net', width: 18, format: (v) => Number(v) },
                  { header: 'Người lập', key: 'createdBy', width: 20 },
                  { header: 'Ngày lập', key: 'createdAt', width: 12, format: (v) => fmtDateISO(v) },
                  { header: 'Trạng thái', key: 'status', width: 14 },
                ], 'Bảng lương');
                showToast(`Đã xuất ${filtered.length} bảng lương`);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
              Xuất Excel
            </button>
            <div className="pay-cols-wrap" ref={columnsWrapRef}>
              <button className="pay-btn-outline" type="button" onClick={() => setColumnsOpen((value) => !value)} aria-expanded={columnsOpen}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 3v18M15 3v18" /></svg>
                Cột hiển thị
              </button>
              {columnsOpen && (
                <div className="pay-cols-menu">
                  <div className="pay-cols-head">Cột hiển thị</div>
                  {(Object.keys(PAYROLL_COLUMN_LABELS) as PayrollColumnKey[]).map((key) => (
                    <label key={key} className="pay-cols-item">
                      <input type="checkbox" checked={columns[key]} onChange={() => setColumns((current) => ({ ...current, [key]: !current[key] }))} />
                      <span>{PAYROLL_COLUMN_LABELS[key]}</span>
                    </label>
                  ))}
                  <div className="pay-cols-foot">
                    <button className="pay-cols-reset" type="button" onClick={() => setColumns(DEFAULT_PAYROLL_COLUMNS)}>Đặt lại</button>
                  </div>
                </div>
              )}
            </div>
            <button className="pay-btn-dark" type="button" onClick={() => setFormOpen(true)}>+ Tạo bảng lương</button>
          </div>
        </div>

        {/* Active filter chips — match Trung tâm phê duyệt */}
        {filterChips.length > 0 && (
          <div className="apr-filter-chips">
            {filterChips.map((c) => (
              <span key={c.key} className="apr-filter-chip">
                {c.label}
                <button type="button" onClick={c.onRemove}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </span>
            ))}
            <button
              type="button"
              className="apr-filter-clear"
              onClick={() => { setFilters({ year: null, status: null, department: null, type: null }); setSearch(''); }}
            >Xoá tất cả</button>
          </div>
        )}

        {selected.length > 0 && (isKtt || isCeo) && (
          <div className="pyr-bulk">
            <div className="pay-bulk-info">
              Đã chọn <b>{selected.length}</b> bảng · Tổng {formatVND(totalSelectedNet)}
            </div>
            <div className="pay-bulk-actions">
              <button className="pay-btn-outline" type="button" onClick={() => { exportSheet('payrolls-selected', selectedRows, [{ header: 'Mã bảng', key: 'code' }, { header: 'Kỳ', key: 'period' }, { header: 'Bộ phận', key: 'department' }, { header: 'Số NV', key: 'employeeCount' }, { header: 'Thực nhận', key: 'net' }, { header: 'Trạng thái', key: 'status' }], 'Bảng lương đã chọn'); showToast(`Đã xuất ${selectedRows.length} bảng lương`); }}>Xuất Excel</button>
              <button className="pay-btn-outline" type="button" onClick={bulkSubmit}>Trình ký hàng loạt</button>
              <button className="pyr-btn-approve-bulk" type="button" onClick={bulkApprove}>
                {isCeo ? 'CEO ký hàng loạt' : 'Duyệt hàng loạt'}
              </button>
              <button className="pay-bulk-clear" type="button" onClick={() => setSelected([])}>Bỏ chọn</button>
            </div>
          </div>
        )}

        <div className="pay-table-wrap pyr-table-wrap">
          {loading ? <div className="pay-empty">Đang tải…</div> : (
            <table className="pay-tbl pyr-tbl">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={selected.length === filtered.length && filtered.length > 0}
                      onChange={(e) => setSelected(e.target.checked ? filtered.map((p) => p.id) : [])}
                    />
                  </th>
                  <th style={{ width: 180 }}>Mã bảng</th>
                  {columns.period && <th style={{ width: 90, textAlign: 'center' }}>Kỳ</th>}
                  {columns.department && <th style={{ width: 140 }}>Bộ phận</th>}
                  {columns.type && <th style={{ width: 160 }}>Loại</th>}
                  {columns.employeeCount && <th style={{ width: 80, textAlign: 'center' }}>Số NV</th>}
                  {columns.basicSalary && <th style={{ width: 160, textAlign: 'right' }}>Lương cơ bản</th>}
                  {columns.net && <th style={{ width: 180, textAlign: 'right' }}>Thực nhận</th>}
                  {columns.creator && <th style={{ width: 160 }}>Người lập</th>}
                  {columns.createdAt && <th style={{ width: 110 }}>Ngày lập</th>}
                  {columns.status && <th style={{ width: 140 }}>Trạng thái</th>}
                  <th style={{ width: 50 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={3 + Object.values(columns).filter(Boolean).length} className="pay-empty">Không có bảng lương khớp bộ lọc.</td></tr>
                )}
                {filtered.map((p) => {
                  const isSel = selected.includes(p.id);
                  return (
                    <tr key={p.id} className={`pyr-row ${isSel ? 'selected' : ''}`} onClick={() => openDetail(p.id)} style={{ cursor: 'pointer' }}>
                      <td onClick={(ev) => ev.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={(e) => setSelected(e.target.checked ? [...selected, p.id] : selected.filter((c) => c !== p.id))}
                        />
                      </td>
                      <td className="pyr-td-code">{p.code}</td>
                      {columns.period && <td style={{ textAlign: 'center', color: '#334155' }}>{p.period}</td>}
                      {columns.department && <td>
                        <div className="pyr-dept-cell">
                          <span className="pyr-dept-dot" style={{ background: deptColor(p.department) }} />
                          <span>{p.department}</span>
                        </div>
                      </td>}
                      {columns.type && <td>
                        <span className="pyr-type-pill">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect width="16" height="20" x="4" y="2" rx="2" /><line x1="8" x2="16" y1="6" y2="6" /><line x1="8" x2="16" y1="10" y2="10" /><line x1="8" x2="16" y1="14" y2="14" /></svg>
                          {p.type}
                        </span>
                      </td>}
                      {columns.employeeCount && <td style={{ textAlign: 'center' }}>
                        <span className="pyr-emp-count">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                          <b>{p.employeeCount}</b>
                        </span>
                      </td>}
                      {columns.basicSalary && <td className="pyr-td-basic">{formatVND(p.basicSalary)}</td>}
                      {columns.net && <td className="pyr-td-net">{formatVND(p.net)}</td>}
                      {columns.creator && <td>
                        <div className="pyr-creator-cell">
                          <div className="pyr-creator-avatar" style={{ background: avatarColor(p.createdBy) }}>{initials(p.createdBy)}</div>
                          <span>{p.createdBy}</span>
                        </div>
                      </td>}
                      {columns.createdAt && <td style={{ color: '#64748b' }}>{new Date(p.createdAt).toLocaleDateString('vi-VN')}</td>}
                      {columns.status && <td><span className={`pyr-status pyr-status-${STATUS_TONE[p.status]}`}>{STATUS_LABEL[p.status]}</span></td>}
                      <td onClick={(ev) => ev.stopPropagation()} style={{ position: 'relative', textAlign: 'right' }}>
                        <button
                          type="button"
                          className="pyr-kebab"
                          onClick={(e) => { e.stopPropagation(); setMenuOpenFor(menuOpenFor === p.id ? null : p.id); }}
                          title="Actions"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></svg>
                        </button>
                        {menuOpenFor === p.id && (
                          <div className="pyr-kebab-menu" onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => { openDetail(p.id); setMenuOpenFor(null); }}>Xem chi tiết</button>
                            <button type="button" onClick={() => { window.print(); setMenuOpenFor(null); }}>In bảng</button>
                            <button type="button" onClick={() => { setMenuOpenFor(null); setFormOpen(true); }}>Tạo bảng lương kỳ mới</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {paging && paging.total > 0 && (
          <div className="pyr-pagination">
            <div className="pyr-page-info">
              Hiển thị {(paging.page - 1) * paging.limit + 1}-{Math.min(paging.page * paging.limit, paging.total)} của {paging.total} bảng lương
            </div>
            <div className="pyr-page-nav">
              <button
                type="button"
                className="hr-page-btn hr-page-nav-btn"
                disabled={paging.page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >◀ Trước</button>
              {pageNumbers(paging.page, paging.totalPages).map((p, i) =>
                p === '…' ? (
                  <span key={`ellipsis-${i}`} className="hr-page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className={`hr-page-btn ${p === paging.page ? 'active' : ''}`}
                    onClick={() => setPage(p as number)}
                  >{p}</button>
                ),
              )}
              <button
                type="button"
                className="hr-page-btn hr-page-nav-btn"
                disabled={!paging.hasNextPage}
                onClick={() => setPage((p) => Math.min(paging.totalPages, p + 1))}
              >Sau ▶</button>
            </div>
            <select
              className="pyr-page-size"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              <option value={10}>10 / trang</option>
              <option value={20}>20 / trang</option>
              <option value={50}>50 / trang</option>
            </select>
          </div>
        )}
      </div>

      <PayrollFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); showToast('Đã tạo bảng lương'); load(); }}
        onOpenExisting={(id) => { setFormOpen(false); openDetail(id); }}
      />

      {filterOpen && (
        <div className="cvm-backdrop" onClick={() => setFilterOpen(false)}>
          <section className="ctf2-modal payroll-filter-modal" style={{ maxWidth: 580 }} onClick={(event) => event.stopPropagation()} aria-modal="true" role="dialog" aria-label="Bộ lọc bảng lương">
            <header className="ctf2-head">
              <div className="ctf2-head-left">
                <div className="ctf2-head-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
                </div>
                <div><h3>Bộ lọc bảng lương</h3><p>Lọc danh sách theo kỳ, trạng thái và phạm vi áp dụng.</p></div>
              </div>
              <button className="ctf2-close" type="button" onClick={() => setFilterOpen(false)} aria-label="Đóng">✕</button>
            </header>
            <div className="ctf2-body">
              <div className="ctf2-card">
                <div className="ctf2-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                  <div className="ctf2-field"><label>Năm</label><SearchableDropdown ariaLabel="Chọn năm" value={String(filterDraft.year ?? '')} options={filterYearOptions} searchPlaceholder="Tìm năm..." onChange={(value) => setFilterDraft((current) => ({ ...current, year: value ? Number(value) : null }))} /></div>
                  <div className="ctf2-field"><label>Trạng thái</label><SearchableDropdown ariaLabel="Chọn trạng thái" value={filterDraft.status ?? ''} options={filterStatusOptions} searchPlaceholder="Tìm trạng thái..." onChange={(value) => setFilterDraft((current) => ({ ...current, status: (value || null) as ActiveFilters['status'] }))} /></div>
                  <div className="ctf2-field"><label>Bộ phận</label><SearchableDropdown ariaLabel="Chọn bộ phận" value={filterDraft.department ?? ''} options={filterDepartmentOptions} searchPlaceholder="Tìm bộ phận..." onChange={(value) => setFilterDraft((current) => ({ ...current, department: value || null }))} /></div>
                  <div className="ctf2-field"><label>Loại bảng lương</label><SearchableDropdown ariaLabel="Chọn loại bảng lương" value={filterDraft.type ?? ''} options={filterTypeOptions} searchPlaceholder="Tìm loại bảng lương..." onChange={(value) => setFilterDraft((current) => ({ ...current, type: value || null }))} /></div>
                </div>
              </div>
            </div>
            <footer className="ctf2-foot" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="ctf2-btn-cancel" onClick={() => setFilterDraft({ year: null, status: null, department: null, type: null })}>Đặt lại</button>
              <div style={{ display: 'flex', gap: 8 }}><button type="button" className="ctf2-btn-cancel" onClick={() => setFilterOpen(false)}>Huỷ</button><button type="button" className="pay-btn-primary" onClick={() => { setFilters(filterDraft); setFilterOpen(false); }}>Áp dụng</button></div>
            </footer>
          </section>
        </div>
      )}

      {toast && <div className="coa-toast">{toast}</div>}
    </AppShell>
  );
}

function pageNumbers(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: Array<number | '…'> = [1];
  if (current > 3) pages.push('…');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('…');
  pages.push(total);
  return pages;
}
